/** @jsxImportSource @emotion/react */
import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { SubTitleLayout, SubTitleGroup, ControlBtnGroup } from 'components/layouts/ContentLayout';
import CustomDialog from 'components/modals/common/CustomDialog';
import {
  ChartContainer,
  ChartTableBody,
  ChartTableHeader,
  GanntChartWrap,
  GanttScroll,
  HorizontalScrollbar,
} from 'components/process/Gannt';
import { Code, CommonCodeCondition } from 'models/common/CommonCode';
import { getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import ImageSlider from 'components/viewer/ImageSlide';
import { API_BASE_URL } from 'utils/ApiUtil';
import { FileImgInfo } from 'models/admin/FileImgInfo';
import { findSetupOnSiteImage } from 'apis/setup/home/SetupProjectSummary';

type Props = {
  open: boolean;
  close: () => void;
  condition: {
    type: 'CMN' | 'DTAL';
    elmPrjId: string; // 프로젝트ID
    tgtEqpId: string; // 대상설비ID
    prjSpCd?: string; // 단계코드 (있는 경우 해당 단계 배경색 표시)
    targetData?: any;
  };
};

/**
 * @desc 설비셋업관리 > 셋업 일정 관리 > 설비 일정 차트 팝업
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.11.17   jshwang12     신규 생성
 * 2025.12.08   jshwang12     타겟 일정 가이드 라인 추가
 * 2026.01.13   jshwang12     프로젝트 단계코드 파라미터 추가 (있는 경우 해당 단계 배경색 표시)
 * 2026.01.19   jshwang12     현장사진 팝업 링크 추가
 * 2026.01.30   jshwang12     단계 배경색 표시 시 자동계산단계('E050', 'M140')인 경우 기구/전장 단계 모두 배경색 표시
 * 2026.05.26   jshwang12     지연 상태 DLY_WRN 추가 (warning)
 * 2026.05.26   jihwan89      시작일과 종료일이 같을 경우 1일로 표기 diffDays결과값에 +1 추가함.
 */
const EquipmentScheduleChartPopup = ({ open, close, condition }: Props) => {
  const { t } = useTranslation();
  const [code, setCode] = useState<any>();
  const [targetData, setTargetData] = useState<any>(condition?.targetData);
  const [minDate, setMinDate] = useState<any>();
  const [maxDate, setMaxDate] = useState<any>();
  const [minPlanDt, setMinPlanDt] = useState<string>();
  const [maxPlanDt, setMaxPlanDt] = useState<string>();
  const [tgtPlanDt, setTgtPlanDt] = useState<string>();
  const [groupingMonth, setGroupingMonth] = useState<any>();
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartTableHeaderRef = useRef<HTMLDivElement>(null);
  const chartTableBodyRef = useRef<HTMLDivElement>(null);
  const ganttScrollRef = useRef<HTMLDivElement>(null);
  const ganttOverlayRef = useRef<HTMLDivElement>(null);
  const ganttHeaderRef = useRef<HTMLDivElement>(null);
  const fixedHeaderWrapperRef = useRef<HTMLDivElement>(null);
  const horizontalScrollbarRef = useRef<HTMLDivElement>(null);
  const horizontalScrollbarContentRef = useRef<HTMLDivElement>(null);
  const CORRECT_START = 1; // 시작 보정값 (시작일이 1일인 경우 일자표시가 짤릴 수 있어서 보정값 1달 추가)
  const CORRECT_END = 1; // 종료 보정값
  const CORRECT_DAY = 3; // 일자 보정값 (월 간격이 좁아서 간격 늘리기 위해 보정값 사용)

  const [isShowImgSlider, setShowImgSlider] = useState<boolean>(false);
  const [imgList, setImgList] = useState<FileImgInfo[]>([]);

  const allMonths = useMemo(() => {
    return Object.keys(groupingMonth || {}).reduce(
      (acc, cur) => [...acc, ...groupingMonth[cur]],
      [] as string[]
    );
  }, [groupingMonth]);

  const title = useMemo(() => {
    if (targetData) {
      const str = [
        targetData?.['PRDN_PROC_CD_NM'],
        targetData?.['EQCL_ID'],
        targetData?.['EQP_NM'],
      ];
      return str.filter((o) => !!o).join(' > ');
    }
    return '';
  }, [targetData]);

  useEffect(() => {
    getCommonCodes();
  }, []);

  useEffect(() => {
    // 설비별 현장사진 목록 조회
    if (condition?.elmPrjId && condition?.tgtEqpId) {
      findSetupOnSiteImage({
        elmPrjId: condition?.elmPrjId,
        tgtEqpId: condition?.tgtEqpId,
      }).then((result) => {
        setImgList(
          (result || []).reduce(
            (acc, cur) => [
              ...acc,
              {
                atchFileGrId: cur?.id,
                atchFileId: cur?.sid,
                dataInsDtm: cur?.dataInsDtm,
              } as FileImgInfo,
            ],
            [] as FileImgInfo[]
          )
        );
      });
    }
  }, [condition?.elmPrjId, condition?.tgtEqpId]);

  useEffect(() => {
    function setGanttHeaderHeight() {
      const container = chartContainerRef.current;
      const header = chartTableHeaderRef.current;
      const ganttHeader = chartTableBodyRef.current;
      const ganttScroll = ganttScrollRef.current;

      if (!container || !header || !ganttHeader || !ganttScroll) return;

      const headerHeight = header.offsetHeight;
      const containerWidth = container.clientWidth;

      ganttHeader.style.height = `${headerHeight}px`;
      ganttScroll.style.height = `${headerHeight - 67}px`; // 필요시 조정
      ganttScroll.style.width = `${containerWidth - 460}px`; // 왼쪽 테이블 너비
    }

    function setGanttOverlayWidth() {
      const header = ganttHeaderRef.current;
      const overlay = ganttOverlayRef.current;
      if (!header || !overlay) return;

      overlay.style.width = `${header.scrollWidth}px`;
    }

    function syncScrollbars() {
      const ganttScroll = ganttScrollRef.current;
      const ganttOverlay = ganttOverlayRef.current;
      const proxyScroll = horizontalScrollbarRef.current;
      const proxyContent = horizontalScrollbarContentRef.current;
      const ganttHeader = ganttHeaderRef.current;
      const fixedHeaderWrapper = fixedHeaderWrapperRef.current;

      if (
        !ganttScroll ||
        !ganttOverlay ||
        !proxyScroll ||
        !proxyContent ||
        !ganttHeader ||
        !fixedHeaderWrapper
      )
        return;

      // 하단 스크롤바 컨텐츠 너비 설정
      proxyContent.style.width = `${ganttOverlay.scrollWidth}px`;

      const updateScroll = (scrollX: number) => {
        ganttScroll.scrollLeft = scrollX;
        proxyScroll.scrollLeft = scrollX;
        ganttHeader.style.transform = `translateX(-${scrollX}px)`;
        fixedHeaderWrapper.style.transform = `translateX(-${scrollX}px)`;
      };

      // 스크롤 이벤트 등록 (중복 등록 방지)
      if (!ganttScroll.dataset.synced) {
        ganttScroll.addEventListener('scroll', () => updateScroll(ganttScroll.scrollLeft));
        proxyScroll.addEventListener('scroll', () => updateScroll(proxyScroll.scrollLeft));
        ganttScroll.dataset.synced = 'true';
      }

      // 초기 동기화
      updateScroll(ganttScroll.scrollLeft);
    }

    function onResizeOrLoad() {
      setGanttHeaderHeight();
      setGanttOverlayWidth();
      syncScrollbars();
    }

    // 렌더 후 DOM 측정 보장
    requestAnimationFrame(() => {
      onResizeOrLoad();
    });

    window.addEventListener('resize', onResizeOrLoad);
    return () => window.removeEventListener('resize', onResizeOrLoad);
  }, [code, minDate, maxDate]);

  const getCommonCodes = async () => {
    let params = { optValCtn5: 'Y' } as CommonCodeCondition; // 일정상세표시여부
    if (condition?.type === 'DTAL' && targetData?.['SP_TP_CD']) {
      params = {
        ...params,
        cmnGrCd: targetData?.['SP_TP_CD'],
      };
    } else {
      // 일정현황에서는 공통단계만 노출
      params = {
        ...params,
        cmnGrCd: 'PRJ_SP_CD',
        optValCtn2: 'Y',
      };
    }
    const prjSpCd: Code[] = await getCommonCodeNamesCondition(params);

    setCode({
      prjSpCd: prjSpCd,
    });
  };

  const convertDate = (str) => {
    return (str || '').replace(/\./g, '-');
  };

  useEffect(() => {
    if (targetData) {
      const arr = Object.keys(targetData)
        .filter(
          (k) =>
            k !== 'TGT_PLAN_END_DT' && (k.endsWith('_PLAN_ST_DT') || k.endsWith('_PLAN_END_DT'))
        )
        .map((k) => targetData[k]) as string[];
      const sorted = (arr || []).sort(
        (a, b) => new Date(convertDate(a)).valueOf() - new Date(convertDate(b)).valueOf()
      );
      const minDt = sorted[0];
      const maxDt = sorted.reverse()[0];
      setMinPlanDt(minDt);
      setMaxPlanDt(maxDt);
      setTgtPlanDt(targetData?.['TGT_PLAN_END_DT'] || '');
    }
  }, [targetData]);

  const getClassNameByStat = (statCd = '') => {
    const stateCodes = {
      PROG: 'ing',
      DLY_WRN: 'warning', // [26.05.26] 지연 상태 DLY_WRN 추가
      DLY: 'delay',
      CPLT: 'done',
      PLAN: 'none',
    };
    return stateCodes[statCd] || '';
  };

  const diffMonth = (startDt, endDt) => {
    return (endDt.year() - startDt.year()) * 12 + (endDt.month() - startDt.month());
  };

  const diffDays = (startDt, endDt, isAtulDays = false) => {
    if (!startDt || !endDt) {
      return 0;
    }
    if (isAtulDays) {
      return endDt.diff(startDt, 'day');
    }
    const y1 = startDt.year();
    const m1 = startDt.month() + 1;
    const d1 = Math.min(startDt.date(), 30); // 편의상 1달 = 30일로 고정

    const y2 = endDt.year();
    const m2 = endDt.month() + 1;
    const d2 = Math.min(endDt.date(), 30);

    // 1달 = 30일로 고정한 상태에서 계산
    const diff = (y2 - y1) * 360 + (m2 - m1) * 30 + (d2 - d1);
    return diff; // 시작일과 종료일이 같은날은 1일이 때문에 +1 추가
  };

  const getGanttBarLeftValue = (startDt) => {
    // (90px * 2) > 월 간격 * 개월수   +  동그라미 시작 위치
    let minDt = dayjs(minDate);
    minDt = minDt.subtract(CORRECT_START, 'month');
    const diff = diffMonth(minDt, startDt);
    const date = startDt.date() < 2 ? 0 : startDt.date();
    return `calc((${30 * CORRECT_DAY}px * ${diff}) + ${date}px * ${CORRECT_DAY})`;
  };

  const getGanttBarWidthValue = (startDt, endDt) => {
    const diff = diffDays(startDt, endDt);
    return `calc(${diff}px * ${CORRECT_DAY})`;
  };

  const delayDays = useMemo(() => {
    const cpltPrjSpCds = (code?.prjSpCd || []).filter(
      (o) => targetData?.[`'${o.cmnCd}'_PRJ_SP_STAT`] === 'CPLT'
    );
    let delayDays = 0; // 지연일수
    // 완료된 단계가 있는 경우
    if (cpltPrjSpCds.length) {
      const lastCd = cpltPrjSpCds[cpltPrjSpCds.length - 1].cmnCd;
      const p = dayjs(targetData?.[`'${lastCd}'_PLAN_END_DT`]);
      const a = dayjs(targetData?.[`'${lastCd}'_ATUL_END_DT`]);
      delayDays = diffDays(p, a);
    } else {
      if ((code?.prjSpCd || []).length) {
        const first = code?.prjSpCd[0].cmnCd;
        const p = dayjs(targetData?.[`'${first}'_PLAN_END_DT`]);
        const a = dayjs(targetData?.[`'${first}'_ATUL_END_DT`]);
        delayDays = Math.max(diffDays(p, a), 0); // 완료된 단계가 없는 상태에서 완료예상일정을 앞당기는 케이스 넣으려면 Math.max 제거
      }
    }
    return delayDays;
  }, [code, targetData]);

  useEffect(() => {
    const planDt = dayjs(maxPlanDt);
    const targetDt = dayjs(tgtPlanDt);
    const max = (targetDt.isAfter(planDt) ? targetDt : planDt)
      .add(Math.max(delayDays, 0), 'day')
      .format('YYYY.MM.DD');
    setMaxDate(max);
  }, [maxPlanDt, tgtPlanDt, delayDays]);

  useEffect(() => {
    if (targetData) {
      const arr = Object.keys(targetData)
        .filter(
          (k) =>
            k !== 'TGT_PLAN_END_DT' &&
            (k.endsWith('_PLAN_ST_DT') ||
              k.endsWith('_PLAN_END_DT') ||
              k.endsWith('_ATUL_ST_DT') ||
              k.endsWith('_ATUL_END_DT'))
        )
        .map((k) => targetData[k]) as string[];
      const sorted = (arr || []).sort(
        (a, b) => new Date(convertDate(a)).valueOf() - new Date(convertDate(b)).valueOf()
      );
      setMinDate(sorted[0]);
    }
  }, [targetData]);

  useEffect(() => {
    const groupMonths = (start, end) => {
      let startDt = dayjs(start);
      let endDt = dayjs(end);
      // 보정
      startDt = startDt.subtract(CORRECT_START, 'month');
      endDt = endDt.add(CORRECT_END, 'month');
      const result = {};
      let cur = startDt.startOf('month');

      while (cur.isBefore(endDt) || cur.isSame(endDt, 'month')) {
        const year = cur.year();
        const month = cur.month() + 1;
        if (!result[year]) result[year] = [];
        if (!result[year].includes(month)) result[year].push(month);
        cur = cur.add(1, 'month');
      }
      return result;
    };
    const gp = groupMonths(minDate, maxDate);
    setGroupingMonth(gp);
  }, [minDate, maxDate]);

  const targetPlanLineLeftValue = useMemo(() => {
    const maxDt = dayjs(tgtPlanDt);
    return getGanttBarLeftValue(maxDt);
  }, [tgtPlanDt]);

  const finalPlanLineLeftValue = useMemo(() => {
    const maxDt = dayjs(maxPlanDt);
    return getGanttBarLeftValue(maxDt);
  }, [maxPlanDt]);

  const finalActualLineLeftValue = useMemo(() => {
    return `calc(${finalPlanLineLeftValue} + (${delayDays}px * ${CORRECT_DAY}))`;
  }, [delayDays, finalPlanLineLeftValue]);

  const drawPlanBar = (prjSpCd: string) => {
    const planStDt = dayjs(targetData?.[`'${prjSpCd}'_PLAN_ST_DT`]);
    const planEndDt = dayjs(targetData?.[`'${prjSpCd}'_PLAN_END_DT`]);
    return (
      <div
        className="gantt-bar plan-bar"
        style={{
          left: getGanttBarLeftValue(planStDt),
          width: getGanttBarWidthValue(planStDt, planEndDt),
        }}
      >
        <span className="date-box date-start">{planStDt.date()}</span>
        <span className="date-box date-end">{planEndDt.date()}</span>
      </div>
    );
  };

  const drawProgressBar = (prjSpCd: string) => {
    const stat = targetData?.[`'${prjSpCd}'_PRJ_SP_STAT`];
    const progRt = targetData?.[`'${prjSpCd}'_PROG_RT`] || 0;
    const planStDt = dayjs(targetData?.[`'${prjSpCd}'_PLAN_ST_DT`]);
    const planEndDt = dayjs(targetData?.[`'${prjSpCd}'_PLAN_END_DT`]);
    let atulStDt = targetData?.[`'${prjSpCd}'_ATUL_ST_DT`];
    let atulEndDt = targetData?.[`'${prjSpCd}'_ATUL_END_DT`];
    if (atulStDt) atulStDt = dayjs(atulStDt);
    if (atulEndDt) atulEndDt = dayjs(atulEndDt);

    const startDt = stat === 'PLAN' || !atulStDt ? planStDt : atulStDt;
    let endDt = stat === 'PLAN' ? planEndDt : atulEndDt;

    // 진행중이면서 완료가 안된 경우
    if (['PROG', 'DLY', 'DLY_WRN'].includes(stat) && !atulEndDt) {
      endDt = dayjs();
    }
    return (
      <div
        className={`gantt-bar progress-bar ${getClassNameByStat(stat)}`}
        style={{
          left: getGanttBarLeftValue(startDt),
          width: getGanttBarWidthValue(startDt, endDt),
        }}
      >
        {/* 0% 인 경우 가운데 text 문제로 progress > 100% 사용 */}
        <span
          className="progress"
          style={{
            width: `${progRt > 0 ? progRt : 100}%`,
          }}
        >
          {Math.floor(Number(progRt) * 10) / 10}%
        </span>
        <span className="date-box date-start">{startDt?.date()}</span>
        <span className="date-box date-end">{endDt?.date()}</span>
      </div>
    );
  };

  const convertStepCodeNm = (cmnCdNm) => {
    if (cmnCdNm) {
      // 코드명에 괄호들어가는 경우 줄바꿈
      return cmnCdNm.replace('(', '<br />(');
    }
    return '';
  };

  return (
    <>
      <CustomDialog
        title={String(t('setup.label.설비 일정 Chart', '설비 일정 Chart'))}
        size={'xxl'}
        open={open}
        onClose={close}
      >
        <style>
          {`
          .cur-step {
            background-color: #FFF8CD80;
          }
        `}
        </style>
        <SubTitleLayout>
          <SubTitleGroup>
            <h3>{title && `${t('setup.label.대상설비', '대상설비')} : ${title}`}</h3>
          </SubTitleGroup>
          <ControlBtnGroup>
            <>
              <label>{t('setup.label.현장사진', '현장사진')}</label>
              <a
                href="javascript:void(0)"
                style={{
                  color: '#2D9BB2',
                  paddingLeft: '2px',
                  textDecoration: 'underline',
                  fontSize: '13px',
                  lineHeight: '1.5',
                }}
                onClick={() => {
                  setShowImgSlider(true);
                }}
              >
                {(imgList || []).length.toLocaleString()}
              </a>
              <label>{t('setup.label.건', '건')}</label>
            </>
          </ControlBtnGroup>
        </SubTitleLayout>

        <GanntChartWrap>
          <ChartContainer ref={chartContainerRef}>
            <ChartTableHeader ref={chartTableHeaderRef}>
              <table>
                <colgroup>
                  <col width={150} />
                  <col width={70} />
                  <col width={80} />
                  <col width={80} />
                  <col width={80} />
                </colgroup>
                <thead>
                  <tr>
                    <th rowSpan={2}>{t('setup.label.단계', '단계')}</th>
                    <th colSpan={4}>{t('setup.label.일정', '일정')}</th>
                  </tr>
                  <tr>
                    <th className="top">{t('setup.label.구분', '구분')}</th>
                    <th className="top">{t('setup.label.시작', '시작')}</th>
                    <th className="top">{t('setup.label.종료', '종료')}</th>
                    <th className="top">{t('setup.label.기간', '기간')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(code?.prjSpCd || []).map((o) => (
                    <>
                      <tr
                        className={
                          condition?.prjSpCd &&
                          (o.cmnCd === condition?.prjSpCd ||
                            (['E050', 'M140'].includes(condition?.prjSpCd) &&
                              o.cmnCd.startsWith(condition.prjSpCd.substring(0, 1))))
                            ? 'cur-step'
                            : ''
                        }
                      >
                        <td
                          rowSpan={2}
                          dangerouslySetInnerHTML={{
                            __html: convertStepCodeNm(o.cmnCdNm),
                          }}
                        ></td>
                        <td className="noBbtt">{t('setup.label.계획', '계획')}</td>
                        <td data-item-id={`'${o.cmnCd}'_PLAN_ST_DT`}>
                          {targetData?.[`'${o.cmnCd}'_PLAN_ST_DT`]}
                        </td>
                        <td data-item-id={`'${o.cmnCd}'_PLAN_END_DT`}>
                          {targetData?.[`'${o.cmnCd}'_PLAN_END_DT`]}
                        </td>
                        <td data-item-id={`'${o.cmnCd}'_PLAN_DT`}>
                          {(
                            (diffDays(
                              dayjs(targetData?.[`'${o.cmnCd}'_PLAN_ST_DT`]),
                              dayjs(targetData?.[`'${o.cmnCd}'_PLAN_END_DT`]),
                              true
                            ) || 0) + 1
                          ).toLocaleString()}
                          {t('setup.label.일', '일')}
                        </td>
                      </tr>
                      <tr
                        className={
                          condition?.prjSpCd &&
                          (o.cmnCd === condition?.prjSpCd ||
                            (['E050', 'M140'].includes(condition?.prjSpCd) &&
                              o.cmnCd.startsWith(condition.prjSpCd.substring(0, 1))))
                            ? 'cur-step'
                            : ''
                        }
                      >
                        <td className="noBtop">{t('setup.label.실적', '실적')}</td>
                        <td data-item-id={`'${o.cmnCd}'_ATUL_ST_DT`}>
                          {targetData?.[`'${o.cmnCd}'_ATUL_ST_DT`]}
                        </td>
                        <td data-item-id={`'${o.cmnCd}'_ATUL_END_DT`}>
                          {targetData?.[`'${o.cmnCd}'_ATUL_END_DT`]}
                        </td>
                        <td data-item-id={`'${o.cmnCd}'_ATUL_DT`}>
                          {targetData?.[`'${o.cmnCd}'_ATUL_ST_DT`] && (
                            <>
                              {(
                                diffDays(
                                  dayjs(targetData?.[`'${o.cmnCd}'_ATUL_ST_DT`]),
                                  dayjs(targetData?.[`'${o.cmnCd}'_ATUL_END_DT`]),
                                  true
                                ) + 1 || 0
                              ).toLocaleString()}
                              {t('setup.label.일', '일')}
                            </>
                          )}
                        </td>
                      </tr>
                    </>
                  ))}
                </tbody>
              </table>
            </ChartTableHeader>
            <ChartTableBody ref={chartTableBodyRef}>
              <div className="fixed-header-wrapper" ref={fixedHeaderWrapperRef}>
                <table>
                  <colgroup>
                    {(allMonths || []).map((s, idx) => (
                      <col key={`fixed_col_${s}_${idx}`} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      {Object.keys(groupingMonth || {}).map((k) => (
                        <th key={k} colSpan={groupingMonth[k].length || 1}>
                          {k}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {(allMonths || []).map((s, idx) => (
                        <th key={`month_${s}_${idx}`} className="top">
                          {s}
                        </th>
                      ))}
                    </tr>
                  </thead>
                </table>
              </div>
              {/*날짜가 나오는 header 와 세로 line 이 있는 body 를 분리하기 위해 빈 tbody 존재
                    /table fixed 를 위해 col 존재 */}
              <div className="gantt-header" ref={ganttHeaderRef}>
                <table>
                  <colgroup>
                    {(allMonths || []).map((s, idx) => (
                      <col key={`gantt_col_${s}_${idx}`} />
                    ))}
                  </colgroup>
                  <tbody>
                    <tr>
                      {(allMonths || []).map((s, idx) => (
                        <td key={`gantt_td_${s}_${idx}`}></td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
              <GanttScroll ref={ganttScrollRef}>
                <div className="gantt-overlay-container" ref={ganttOverlayRef}>
                  {(code?.prjSpCd || []).map((o) => (
                    <div
                      key={`gantt_${o.cmnCd}`}
                      data-prj-sp-cd={o.cmnCd}
                      className={`gantt-bar-layer ${
                        condition?.prjSpCd &&
                        (o.cmnCd === condition?.prjSpCd ||
                          (['E050', 'M140'].includes(condition?.prjSpCd) &&
                            o.cmnCd.startsWith(condition.prjSpCd.substring(0, 1))))
                          ? 'cur-step'
                          : ''
                      }`}
                    >
                      {drawPlanBar(o.cmnCd)}
                      {drawProgressBar(o.cmnCd)}
                    </div>
                  ))}
                  <div
                    className="final-timeline-indicators"
                    style={{ left: `${targetPlanLineLeftValue}` }}
                  >
                    <div className="target-plan-line">
                      <span>{t('setup.label.Target 일정', 'Target 일정')}</span>
                    </div>
                  </div>
                  <div
                    className="final-timeline-indicators"
                    style={{ left: `${finalPlanLineLeftValue}` }}
                  >
                    <div className="final-plan-line">
                      <span>{t('setup.label.계획완료일정', '계획완료일정')}</span>
                    </div>
                  </div>
                  <div
                    className="final-timeline-indicators"
                    style={{ left: `${finalActualLineLeftValue}` }}
                  >
                    <div className="final-actual-line">
                      <span>{t('setup.label.예상완료일정', '예상완료일정')}</span>
                    </div>
                  </div>
                </div>
              </GanttScroll>
            </ChartTableBody>
          </ChartContainer>
          <HorizontalScrollbar ref={horizontalScrollbarRef}>
            <div className="scrollbar-content" ref={horizontalScrollbarContentRef}></div>
          </HorizontalScrollbar>
        </GanntChartWrap>
      </CustomDialog>
      <CustomDialog
        title={`${targetData?.['EQP_NM'] ?? ''}`}
        open={isShowImgSlider}
        onClose={() => setShowImgSlider(false)}
      >
        <ImageSlider
          images={imgList.map((o) => {
            const url = `${API_BASE_URL}/v1/file-image/view`;

            const params = new URLSearchParams({
              atchFileGrId: o.atchFileGrId ?? undefined,
              atchFileId: o.atchFileId ?? undefined,
            });

            return {
              img: params.toString() ? `${url}?${params.toString()}` : url,
              date: o.dataInsDtm ?? null,
            };
          })}
          alt={`${targetData?.['EQP_ID'] ?? 'No'} image`}
        />
      </CustomDialog>
    </>
  );
};

export default EquipmentScheduleChartPopup;
